import React, { ChangeEvent } from 'react';
import { Box, Theme, Grid, Typography, Divider } from '@material-ui/core';
import { useFormikContext } from 'formik';
import { Select } from '@campfire/select';
import { MultiSelect } from '@campfire/multi-select';
import { makeStyles, createStyles } from '@material-ui/styles';
import { CreatWidgetFormValues, DimensionEnum, dimensionLabels, WidgetTypes } from '../../dashboard-widget-model';
import { useCampfireTheme } from '../../../../../../../../theme/useCampfireTheme';
import { useWidgetContextForm } from '../../useWidgetContextForm';
import { SelectedValues } from '../SelectedValues';
import {
  FieldValueFilters,
  fieldValueDimensions,
  useFieldValueSource,
  useSelectedFieldOptions,
} from '../FieldValueFilters';
import { useApplyCustomWords } from '../../../useCustomWords';
import { TimeSettings } from '../TimeSettings';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    title: {
      color: theme.color.grey.neutral500,
      fontWeight: 700,
      fontSize: '18px',
      marginBottom: '8px',
    },
    label: {
      fontSize: '14px',
      fontWeight: 700,
      color: theme.color.grey.neutral500,
      paddingBottom: '6px',
    },
    valueItem: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      height: '56px',
      borderRadius: '6px',
      marginTop: '-8px',
      padding: '0 16px',
      cursor: 'pointer',
      position: 'relative',

      border: `1px solid ${theme.color.grey.neutralBrand200}`,
    },
  })
);

interface Props {
  maxValue?: number;
}

const dimensionOptions = [DimensionEnum.BY_PROGRAM, DimensionEnum.BY_ACTIVITY, DimensionEnum.BY_ACTIVITY_TAG];

const incidentOptions = [
  DimensionEnum.BY_PROGRAM,
  DimensionEnum.BY_INCIDENT_CATEGORY,
  DimensionEnum.BY_INCIDENT_DETAIL,
  DimensionEnum.BY_INCIDENT_GROUP,
  DimensionEnum.BY_INCIDENT_LOCATION,
  DimensionEnum.BY_INCIDENT_SEVERITY,
];
const volunteerOptions = [DimensionEnum.BY_PROGRAM];

export const BarChartWidgetForm = ({ maxValue = 5 }: Props) => {
  const cw = useApplyCustomWords();
  const { theme } = useCampfireTheme();
  const classes = useStyles();
  const { values, setFieldValue, handleChange } = useFormikContext<CreatWidgetFormValues>();

  const programOption = values.byValues.some(({ tag }) => ['programs'].includes(tag));
  const incidentOption = values.byValues.some(({ tag }) => tag === 'incidents');
  const volunteerOption = values.byValues.some(({ tag }) => tag === 'volunteers');
  const fieldValueSource = useFieldValueSource();
  const fieldOptions = useSelectedFieldOptions();

  const dimensions = React.useMemo(() => {
    if (fieldValueSource) {
      return [DimensionEnum.BY_TIME].concat(fieldValueDimensions(fieldValueSource, fieldOptions.length > 0));
    }

    if (programOption) {
      return [DimensionEnum.BY_TIME];
    }

    if (incidentOption) {
      return [DimensionEnum.BY_TIME].concat(incidentOptions);
    }

    if (volunteerOption) {
      return [DimensionEnum.BY_TIME].concat(volunteerOptions);
    }

    return [DimensionEnum.BY_TIME].concat(dimensionOptions);
  }, [programOption, incidentOption, volunteerOption, fieldValueSource, fieldOptions.length]);

  const dimensionItems = dimensions.map((value) => ({
    value,
    label: cw(dimensionLabels[value]),
  }));

  const groups = React.useMemo(() => {
    if (fieldValueSource) {
      return fieldValueDimensions(fieldValueSource, fieldOptions.length > 0);
    }

    if (programOption) {
      return [];
    }

    if (incidentOption) {
      return incidentOptions;
    }

    if (volunteerOption) {
      return volunteerOptions;
    }

    return dimensionOptions;
  }, [programOption, incidentOption, volunteerOption, fieldValueSource, fieldOptions.length]);

  const groupOptions = [
    {
      label: 'None (time only)',
      value: 'none',
    },
  ].concat(
    groups.map((value) => ({
      value,
      label: cw(dimensionLabels[value]),
    }))
  );

  const handleChangeDimension = (e: ChangeEvent<HTMLInputElement>) => {
    setFieldValue('dimension', e.target.value);
    setFieldValue('programIds', []);
    setFieldValue('activityIds', []);
    setFieldValue('tagIds', []);
  };
  const showInterval = values.dimension === DimensionEnum.BY_TIME;

  React.useEffect(() => {
    if (fieldValueSource && values.dimension !== null && !dimensions.includes(values.dimension)) {
      setFieldValue('dimension', DimensionEnum.BY_TIME);
    }
  }, [dimensions]);

  const { programs, activities, activityTags } = useWidgetContextForm();

  const showHighlight = values.byType === WidgetTypes.NUMBER_CHART;

  return (
    <Grid container direction='column' style={{ width: '100%', marginBottom: '16px' }}>
      <Grid item xs={12}>
        <Typography className={classes.title}>Filtering</Typography>
        <Grid container spacing={3}>
          <Grid item xs={6}>
            <Typography className={classes.label}>Dimension</Typography>
            <Select
              fullWidth
              key={dimensions.join()}
              name='dimension'
              variant='outlined'
              items={dimensionItems}
              renderFunction={(item) => item.label}
              valueFunction={(item) => item.value}
              InputProps={{
                style: { borderRadius: '8px', color: theme.color.grey.neutral300 },
              }}
              onChange={handleChangeDimension}
            />
          </Grid>
          {values.dimension === DimensionEnum.BY_TIME && (
            <Grid item xs={6}>
              <Typography className={classes.label}>Group by</Typography>
              <Select
                fullWidth
                variant='outlined'
                name='group'
                items={groupOptions}
                InputProps={{
                  style: { borderRadius: '8px', color: theme.color.grey.neutral300 },
                }}
                renderFunction={(item) => item.label}
                valueFunction={(item) => item.value}
                onChange={handleChange}
              />
            </Grid>
          )}
          {values.dimension === DimensionEnum.BY_PROGRAM && (
            <Grid item xs={6}>
              <Typography className={classes.label}>{cw('Programs')}</Typography>
              <MultiSelect
                variant='outlined'
                name='programIds'
                label={cw('Select program/s')}
                placeholder={cw('All Programs')}
                items={programs}
                style={{ borderRadius: '8px' }}
              />
            </Grid>
          )}
          {values.dimension === DimensionEnum.BY_ACTIVITY && (
            <Grid item xs={6}>
              <Typography className={classes.label}>{cw('Activities')}</Typography>
              <MultiSelect
                variant='outlined'
                name='activityIds'
                label={cw('Select activity/activities')}
                placeholder={cw('All Activities')}
                items={activities}
                style={{ borderRadius: '8px' }}
              />
            </Grid>
          )}
          {values.dimension === DimensionEnum.BY_ACTIVITY_TAG && (
            <Grid item xs={6}>
              <Typography className={classes.label}>{cw('Activity Tags')}</Typography>
              <MultiSelect
                variant='outlined'
                name='tagIds'
                placeholder={cw('All Activity Tags')}
                label='Select tag/s'
                items={activityTags}
                style={{ borderRadius: '8px' }}
              />
            </Grid>
          )}
          <FieldValueFilters labelClassName={classes.label} withPrograms />
        </Grid>
      </Grid>
      <Box my={3}>
        <Divider />
      </Box>
      <TimeSettings showHighlight={showHighlight} showInterval={showInterval} />
      <Box my={3}>
        <Divider />
      </Box>
      <SelectedValues maxValue={maxValue} />
    </Grid>
  );
};
